import { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import useAuth from '@vl-core/useAuth';
import moment from 'moment-timezone';
import { ToDoItem } from '@components/Patients/ToDos/ToDos';
import useNeedCardDetails from '@hooks/useNeedCardDetails';
import useFileDownloader from '@hooks/useFileDownloader';
import useIsRestricted from './useIsRestricted';
import { usePleaseLogIn } from './usePleaseLogIn';
import useActiveCases from './useActiveCases';
import useBookingFlowControl from 'vl-common/src/hooks/useBookingFlowControl';
import { getCurrentLocation } from 'vl-common/src/lib/getCurrentLocation';
import { useTranslation } from 'react-i18next';
import { postExercisePlanComplete } from '@actions';
import { useDispatch } from 'react-redux';

const useTodoActions = (
  todos: ToDoItem[],
  showAvailabilityModal?: (caseId: number | null, apptType: string) => void,
  refetchTodos?: () => void
): {
  text: string;
  action?: () => void;
  test_id?: string;
}[] => {
  const dispatch = useDispatch();
  const { initializeF2FBooking } = useBookingFlowControl();
  const { user } = useAuth();
  const isRestricted = useIsRestricted();
  const { showNeedCardDetailsOrRedirectToSurvey } = useNeedCardDetails();
  const { openFileInNewTab } = useFileDownloader();
  const { activeCases, updateActiveCases } = useActiveCases();
  const casesById = useMemo(
    () => Object.fromEntries(activeCases?.map((activeCase) => [activeCase.case_id, activeCase]) ?? []),
    [activeCases]
  );
  const { t: tBE } = useTranslation('commonBackendKeys');
  const { initializeNewAppointmentBooking } = useBookingFlowControl();

  const router = useRouter();
  if (user) {
    moment.tz.setDefault(user.timezone);
  }

  const [, setPleaseLogInModalOpen] = usePleaseLogIn();
  const showPleaseLoginModal = () => setPleaseLogInModalOpen(true);

  const navigateToBooking = (todo: ToDoItem) => {
    if (!user || !todo.appt_type_code || !todo.user_subtype_code) return;
    const { pathname, search } = getCurrentLocation();

    initializeNewAppointmentBooking({
      caseId: todo.case_id,
      patientId: user.user_guid,
      apptType: todo.appt_type_code,
      clinType: todo.user_subtype_code,
      apptAuthFromDate: todo.appt_auth_from_date,
      apptAuthEndDate: todo.appt_auth_end_date,
      redirectRoute: `${pathname}${search}`
    });
    router.push('/patients/choose');
  };

  const substituteDateTime = (todoText: string, dateTime: Date) => {
    const formattedDateTime = moment(dateTime).format('DD/MM/YY HH:mm');
    return todoText.replace(/{{datetime}}/g, formattedDateTime);
  };

  useEffect(() => {
    updateActiveCases();
  }, []);

  return useMemo(() => {
    return todos
      ?.map((todo) => {
        const isComplete = todo.status === 'Complete';
        const isNotEditableForCurrentUser = !todo.survey_guest_editable && isRestricted;

        // Handle attend appointment
        switch (todo.todo_type) {
          case 'uappt':
            if (isComplete) {
              return {
                text: substituteDateTime(todo.action_desc, todo.datetime),
                test_id: todo.test_id
              };
            }
            return {
              text: substituteDateTime(todo.action_desc, todo.datetime),
              action: () => router.push(`/patients/appointments?caseId=${todo.case_id}`),
              test_id: todo.test_id
            };

          // Handle survey
          case 'surveyjs':
            if (isComplete) return { text: todo.action_desc };

            if (isNotEditableForCurrentUser) {
              return {
                text: todo.action_desc,
                action: () => showPleaseLoginModal?.(),
                test_id: todo.test_id
              };
            }

            return {
              text: tBE(todo.action_desc),
              action: () =>
                showNeedCardDetailsOrRedirectToSurvey(
                  todo.survey_id || 0,
                  'todos'
                  /*, todo.card_details_mandatory || false*/
                ),
              test_id: todo.test_id
            };

          // case 'stripe':
          //   // Handle Stripe
          //   if (isComplete) return { text: todo.action_desc };

          //   return {
          //     text: todo.action_desc,
          //     test_id: todo.test_id,
          //     action: () => router.push('/patients/profile')
          //   };

          // Handle external booking
          case 'eauth':
            if (isComplete) return { text: todo.action_desc, test_id: todo.test_id };

            return {
              text: todo.action_desc,
              action: () => {
                if (isRestricted && todo.user_subtype_code === 'PHYSIO') {
                  showPleaseLoginModal?.();
                  return;
                }

                initializeF2FBooking({
                  caseId: todo?.case_id,
                  patientId: user?.user_guid || '',
                  previousApptId: todo.appt_id!,
                  apptAuthFromDate: todo.appt_auth_from_date!,
                  apptType: todo.appt_type_code!
                });

                router.push(`/patients/face2face?previous=${isRestricted ? 'dashboard' : 'todos'}`).then();
              },
              test_id: todo.test_id
            };

          // Handle internal booking
          case 'aauth':
            if (isComplete) return { text: todo.action_desc, test_id: todo.test_id };

            return {
              text: todo.action_desc,
              action: () => {
                const latestCaseDetails = casesById[todo.case_id];

                const canBookConsultantAppointmentLatest =
                  latestCaseDetails?.patient_appt_quota > 0 &&
                  latestCaseDetails?.patient_clin_subtype_code === 'CONSULT';

                if (
                  !canBookConsultantAppointmentLatest &&
                  todo.user_subtype_code === 'CONSULT' &&
                  !(todo.spec_cat_code === 'MSK' && todo.appt_type_code === 'IMG-REVIEW') &&
                  showAvailabilityModal
                ) {
                  showAvailabilityModal(todo.case_id, todo.appt_type_code || '');
                  return;
                }

                navigateToBooking(todo);
              },
              test_id: todo.test_id
            };
          case 'clinsum':
            if (isComplete) return { text: substituteDateTime(tBE(todo.action_desc), todo.datetime) };
            return {
              text: substituteDateTime(tBE(todo.action_desc), todo.datetime),
              action: () => {
                if (user && todo.file_guid) {
                  openFileInNewTab(user.user_guid, todo.file_guid).then();
                }
                sessionStorage.setItem('case-filter-selected', String(todo.case_id));
                router.push('/patients/documents').then();
              }
            };

          // Handles exercise plan: opens URL in new tab and posts completed status
          case 'explan': {
            return {
              text: tBE(todo.action_desc),
              test_id: todo.test_id,
              action: async () => {
                if (todo.file_guid) {
                  window.open(todo.file_guid, '_blank');
                }

                if (!isComplete && user?.user_guid) {
                  try {
                    await dispatch(postExercisePlanComplete(user.user_guid, todo.case_id));
                    refetchTodos?.();
                  } catch (error) {
                    console.error('Failed to update exercise plan flag:', error);
                  }
                }
              }
            };
          }

          case 'nocase':
            if (isComplete) return { text: todo.action_desc, test_id: todo.test_id };

            return {
              text: todo.action_desc,
              action: () => router.push('/patients/?createCase=true'),
              user_subtype_code: todo.user_subtype_code,
              test_id: todo.test_id
            };

          default:
            return { text: tBE(todo.action_desc), test_id: todo.test_id, user_subtype_code: todo.user_subtype_code };
        }
      })
      .map(({ text, action, test_id, user_subtype_code }) => ({
        text: text || '',
        action,
        test_id: test_id || undefined,
        user_subtype_code: user_subtype_code || undefined
      }));
  }, [todos, casesById, user, isRestricted, showAvailabilityModal, tBE]);
};

export default useTodoActions;
